import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { FormInput } from '@podus/ui';
import AuthLayout from '@/components/AuthLayout';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

interface LoginResponse {
    token: string;
    data: {
        id: number;
        name: string | null;
        email: string;
        code: string | null;
        referralLink: string | null;
    };
}

/** Ambassador sign-in — email + password, held on this platform only. */
export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const disabled = !email.trim() || !password || busy;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (disabled) return;

        setError('');
        setBusy(true);
        try {
            const res = await api.post<LoginResponse>('/ambassador/login', {
                email: email.trim().toLowerCase(),
                password,
            });
            const { token, data } = res.response;

            login(token, {
                id: data.id,
                name: data.name,
                email: data.email,
                role: 'ambassador',
                code: data.code,
                referralLink: data.referralLink,
            });
            navigate('/dashboard', { replace: true });
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Could not sign you in. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthLayout
            title="Sign in to your ambassador account"
            subtitle="Use the email address your invitation was sent to."
        >
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {error && (
                    <div
                        role="alert"
                        className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
                    >
                        {error}
                    </div>
                )}

                <FormInput
                    id="ab-email"
                    name="email"
                    type="email"
                    title="Email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    variant="white"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />

                <FormInput
                    id="ab-password"
                    name="password"
                    type="password"
                    title="Password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    variant="white"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />

                <div className="flex justify-end">
                    <Link to="/forgot-password" className="text-[13px] font-semibold text-primary">
                        Forgot password?
                    </Link>
                </div>

                <button
                    type="submit"
                    disabled={disabled}
                    className="mt-2 h-12 w-full rounded-[10px] bg-secondary text-[14px] font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {busy ? 'Signing in…' : 'Sign in'}
                </button>
            </form>
        </AuthLayout>
    );
}
